import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";

/**
 * Inline "click-to-edit" field.
 *
 * States:
 *   idle     — value + pencil reveal on hover/focus
 *   editing  — input / textarea / select bound to a local draft
 *   saving   — input disabled + pulsing dot + optimistic update applied upstream
 *
 * Keystrokes: ``Enter`` saves, ``Esc`` cancels, blur saves if dirty. Textareas
 * take ``Shift+Enter`` for newline and ``Enter`` alone saves.
 *
 * While the field is in edit mode, remote live updates to the same path are
 * ignored (the parent should pass ``value`` only when ``isEditing`` is false —
 * see ``ContactDetailPage`` wiring). That protects the user's draft.
 */
export type EditableVariant = "text" | "textarea" | "email" | "url" | "tel";

export type SelectOption = { value: string; label?: string };

export function EditableField({
  value,
  label,
  placeholder,
  variant = "text",
  options,
  onSave,
  renderValue,
  disabled,
}: {
  value: string | null | undefined;
  label: string;
  placeholder?: string;
  variant?: EditableVariant | "select";
  options?: SelectOption[];
  onSave: (next: string) => Promise<unknown>;
  renderValue?: (value: string | null | undefined) => React.ReactNode;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      } else if (inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length,
        );
      }
    }
  }, [editing]);

  const startEdit = () => {
    if (disabled || editing) return;
    setError(null);
    setDraft(value ?? "");
    setEditing(true);
  };

  const commit = async () => {
    const next = draft.trim();
    const prev = (value ?? "").trim();
    if (next === prev) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(value ?? "");
    setError(null);
    setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter" && (variant !== "textarea" || !e.shiftKey)) {
      e.preventDefault();
      void commit();
    }
  };

  if (!editing) {
    const displayed =
      renderValue ? renderValue(value) : value || <span className="text-ink-low">—</span>;
    return (
      <div>
        <button
          type="button"
          className="editable"
          aria-label={`Edit ${label}`}
          onClick={startEdit}
          onFocus={startEdit}
          disabled={disabled}
          style={{ textAlign: "left", width: "100%" }}
        >
          <span>{displayed}</span>
          <span className="pencil" aria-hidden>
            ✎
          </span>
        </button>
        {error ? <span className="editable-error">{error}</span> : null}
      </div>
    );
  }

  const commonProps = {
    className: "input",
    disabled: saving,
    onKeyDown,
    onBlur: () => void commit(),
    "aria-label": label,
  } as const;

  return (
    <div className={clsx("editable-wrapper")} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
      <div style={{ flex: 1 }}>
        {variant === "textarea" ? (
          <textarea
            {...commonProps}
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            style={{ resize: "vertical", minHeight: 72 }}
          />
        ) : variant === "select" ? (
          <select
            {...commonProps}
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          >
            <option value="">—</option>
            {(options || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label ?? o.value}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...commonProps}
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type={variant === "email" ? "email" : variant === "tel" ? "tel" : variant === "url" ? "url" : "text"}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
          />
        )}
        {error ? <span className="editable-error">{error}</span> : null}
      </div>
      {saving ? <span className="save-dot" aria-label="saving" /> : null}
    </div>
  );
}
